import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { Category, FaqItem, Media, Post, SiteSettings } from '@nhanho/shared'
import { slugifyVi } from '@nhanho/shared'
import { posts as api, categories as categoriesApi, settings as settingsApi, users as usersApi, explainError, type AdminUserRow, type PostBody } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useToast } from '../../lib/toast'
import { useDirtyGuard } from '../../lib/dirty'
import { PageHeader } from '../../components/ui/PageHeader'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Field } from '../../components/ui/Field'
import { Input, Select, Textarea } from '../../components/ui/Input'
import { Toggle } from '../../components/ui/Toggle'
import { TagInput } from '../../components/ui/TagInput'
import { Spinner } from '../../components/ui/Spinner'
import { ConfirmDialog } from '../../components/ui/Modal'
import { ImageField } from '../../components/media/ImageField'
import { RichTextEditor } from '../../components/editor/RichTextEditor'
import { SeoPanel, EMPTY_SEO, seoFromMeta, seoToMeta, type SeoValue } from '../../components/seo/SeoPanel'
import { FaqEditor } from '../../components/seo/FaqEditor'

interface Form {
  title: string; slug: string; slugTouched: boolean; excerpt: string; content: string; featuredImage: Media | null
  /** Id của `User`. Rỗng = chưa gán (bài cũ chưa migrate). */
  category: string; authorUser: string; tags: string[]; publishedAt: string; published: boolean
  /** @deprecated Chuỗi tác giả cũ — chỉ ĐỌC, để người biên tập thấy bài chưa migrate. */
  legacyAuthor: string
  faq: FaqItem[]; seo: SeoValue
}
const EMPTY: Form = { title: '', slug: '', slugTouched: false, excerpt: '', content: '', featuredImage: null, category: '', authorUser: '', tags: [], publishedAt: '', published: false, faq: [], seo: EMPTY_SEO, legacyAuthor: '' }

const fromDoc = (p: Post): Form => ({
  title: p.title, slug: p.slug, slugTouched: true, excerpt: p.excerpt ?? '', content: p.content,
  featuredImage: typeof p.featuredImage === 'object' ? (p.featuredImage as Media | null) : null,
  // `category` populate về object; form chỉ giữ id.
  category: typeof p.category === 'object' && p.category ? p.category.id : (p.category as string | null) ?? '',
  // `authorUser` populate về object; form chỉ giữ id — cùng mẹo với `category`.
  authorUser: typeof p.authorUser === 'object' && p.authorUser ? p.authorUser.id : (p.authorUser as string | null) ?? '',
  legacyAuthor: p.author ?? '', tags: p.tags ?? [],
  publishedAt: p.publishedAt ? p.publishedAt.slice(0, 10) : '', published: p.published,
  faq: (p.faq ?? []).map((f) => ({ question: f.question, answer: f.answer })),
  seo: seoFromMeta(p.meta),
})

/** `slug` chỉ gửi khi user tự sửa — không sửa thì server tự sinh và tự tránh trùng. */
const toBody = (f: Form, isAdmin: boolean): PostBody => ({
  title: f.title, slug: f.slugTouched && f.slug ? f.slug : undefined, excerpt: f.excerpt, content: f.content,
  featuredImage: f.featuredImage?.id ?? null, category: f.category || null, tags: f.tags,
  /**
   * Chỉ admin gửi khoá này. Editor để `undefined` ⇒ biến mất khỏi `JSON.stringify` ⇒
   * server tự gán người đang đăng nhập. Thẩm quyền nằm đúng một chỗ (API), không phải hai.
   */
  authorUser: isAdmin ? (f.authorUser || null) : undefined,
  publishedAt: f.publishedAt ? new Date(f.publishedAt).toISOString() : null, published: f.published,
  faq: f.faq.filter((x) => x.question.trim() && x.answer.trim()),
  meta: seoToMeta(f.seo),
})

export default function PostForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [form, setForm] = useState<Form | null>(id ? null : EMPTY)
  const [initial, setInitial] = useState<Form | null>(id ? null : EMPTY)
  const [site, setSite] = useState<SiteSettings | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [del, setDel] = useState(false)
  const [confirmSlug, setConfirmSlug] = useState<null | (() => void)>(null)
  const [confirmHide, setConfirmHide] = useState(false)
  const [tab, setTab] = useState<'content' | 'seo'>('content')
  const [blogCats, setBlogCats] = useState<Category[]>([])
  const { user, isAdmin, isManager } = useAuth()
  const [people, setPeople] = useState<AdminUserRow[]>([])

  useEffect(() => { settingsApi.get().then(setSite).catch(() => setSite(null)) }, [])
  /** Danh mục blog = `categories` có `space: 'post'` (đổi 2026-09-11, trước là enum cứng). */
  useEffect(() => {
    categoriesApi.list({ space: 'post', published: 'all', limit: 200, sort: 'order,name' })
      .then((r) => setBlogCats(r.docs))
      .catch(() => setBlogCats([]))
  }, [])
  /**
   * Danh sách tác giả cho dropdown — **chỉ admin**. `GET /api/users` là `requireAdmin`,
   * editor gọi sẽ ăn 403 và bắn toast lỗi vô cớ trên một form vẫn dùng được bình thường.
   */
  useEffect(() => {
    if (!isAdmin) return
    usersApi.list({ limit: 100, sort: 'name' })
      .then((r) => setPeople(r.docs))
      .catch(() => setPeople([]))
  }, [isAdmin])
  useEffect(() => {
    if (!id) return
    api.get(id).then((p) => { const f = fromDoc(p); setForm(f); setInitial(f) })
      .catch((e) => { toast.error(explainError(e).message); navigate('/posts') })
  }, [id, navigate, toast])

  const dirty = useMemo(() => !!form && !!initial && JSON.stringify(toBody(form, isAdmin)) !== JSON.stringify(toBody(initial, isAdmin)), [form, initial, isAdmin])
  useDirtyGuard(dirty)

  if (!form) return <Spinner />
  const patch = (p: Partial<Form>) => setForm((f) => (f ? { ...f, ...p } : f))
  const setTitle = (title: string) => patch({ title, slug: form.slugTouched ? form.slug : slugifyVi(title) })

  const doSave = async (next: Form) => {
    setBusy(true); setErrors({})
    try {
      const saved = id ? await api.update(id, toBody(next, isAdmin)) : await api.create(toBody(next, isAdmin))
      const f = fromDoc(saved)
      setForm(f); setInitial(f)
      toast.success(saved.published ? 'Đã lưu — bài đang hiển thị trên web' : 'Đã lưu (bài đang ẩn)')
      if (!id) navigate(`/posts/${saved.id}`, { replace: true })
    } catch (e) {
      const { message, fieldErrors } = explainError(e)
      setErrors(fieldErrors)
      toast.error(message)
      if (Object.keys(fieldErrors).some((k) => k.startsWith('meta') || k.startsWith('faq'))) setTab('seo')
    } finally { setBusy(false) }
  }

  /** Đổi slug của bài đã đăng = gãy link cũ → hỏi trước. */
  const save = (next: Form = form) => {
    if (initial?.published && id && next.slug !== initial.slug) { setConfirmSlug(() => () => void doSave(next)); return }
    void doSave(next)
  }

  const remove = async () => {
    if (!id) return
    setBusy(true)
    try { await api.remove(id); toast.success('Đã xoá'); setInitial(form); navigate('/posts') }
    catch (e) { toast.error(explainError(e).message) } finally { setBusy(false) }
  }

  return (
    <>
      <PageHeader title={id ? 'Sửa bài viết' : 'Bài viết mới'} subtitle={form.slug ? `/blog/${form.slug}` : undefined}
        actions={<>
          {id && <Badge tone={initial?.published ? 'success' : 'neutral'}>{initial?.published ? 'Đang hiển thị' : 'Đang ẩn'}</Badge>}
          {/* `DELETE /api/posts/:id` là `requireManager` — editor không xoá được bài. */}
          {id && isManager && <Button variant="ghost" className="text-danger" onClick={() => setDel(true)}>Xoá</Button>}
          {form.published
            ? <Button variant="secondary" onClick={() => setConfirmHide(true)} loading={busy}>Gỡ khỏi web</Button>
            : <Button variant="secondary" onClick={() => save({ ...form, published: true })} loading={busy}>Lưu & đăng</Button>}
          <Button onClick={() => save()} loading={busy}>{dirty ? 'Lưu' : 'Đã lưu'}</Button>
        </>} />

      <div className="mb-4 flex gap-1 border-b border-border">
        {(['content', 'seo'] as const).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === t ? 'border-primary-700 text-primary-950' : 'border-transparent text-ink-muted hover:text-ink'}`}>{t === 'content' ? 'Nội dung' : 'SEO'}</button>
        ))}
      </div>

      {tab === 'content' ? (
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-5">
            <Card>
              <div className="space-y-4">
                <Field label="Tiêu đề" required error={errors.title}><Input value={form.title} onChange={(e) => setTitle(e.target.value)} className="text-base font-medium" /></Field>
                <Field label="Slug" required hint="Đường dẫn: /blog/<slug>. Đổi sau khi đã đăng thì hệ thống tự tạo 301, nhưng Google vẫn cần thời gian chuyển thứ hạng." error={errors.slug}><Input value={form.slug} onChange={(e) => patch({ slug: e.target.value, slugTouched: true })} className="font-mono" /></Field>
                <Field label="Tóm tắt" hint="Hiện ở danh sách bài và meta description nếu để trống bên SEO" error={errors.excerpt}><Textarea value={form.excerpt} onChange={(e) => patch({ excerpt: e.target.value })} maxLength={500} /></Field>
              </div>
            </Card>
            <Card title="Nội dung">
              {errors.content && <p className="mb-2 text-xs text-danger">{errors.content}</p>}
              <RichTextEditor content={form.content} onChange={(html) => patch({ content: html })} resetKey={id} placeholder="Viết bài…" />
            </Card>
          </div>
          <div className="space-y-5">
            <Card title="Xuất bản">
              <div className="space-y-4">
                <Toggle checked={form.published} onChange={(v) => patch({ published: v })} label={form.published ? 'Đang hiển thị trên web' : 'Ẩn khỏi web'} />
                <Field label="Ngày đăng" hint="Để trống → tự lấy lúc đăng lần đầu"><Input type="date" value={form.publishedAt} onChange={(e) => patch({ publishedAt: e.target.value })} /></Field>
                <Field label="Chuyên mục" hint={blogCats.length === 0 ? 'Chưa có danh mục blog nào — tạo ở mục Danh mục, tab Blog' : `Trang đích: /blog/<slug>`}><Select value={form.category} onChange={(e) => patch({ category: e.target.value })}><option value="">—</option>{blogCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
                {isAdmin ? (
                  <Field label="Tác giả" hint="Trang tác giả: /tac-gia/<slug>">
                    <Select value={form.authorUser} onChange={(e) => patch({ authorUser: e.target.value })}>
                      <option value="">—</option>
                      {people.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </Select>
                  </Field>
                ) : (
                  /*
                    Editor: dòng tĩnh, KHÔNG phải <Select disabled>. Một control không bao giờ
                    bật được chỉ là nhiễu. API cũng chặn đúng như vậy (`resolveAuthorUser`).
                  */
                  <Field label="Tác giả">
                    <p className="text-sm text-ink">{form.authorUser ? (people.find((u) => u.id === form.authorUser)?.name ?? user?.name) : user?.name}</p>
                    <p className="mt-0.5 text-xs text-ink-muted">Bài viết ghi tên bạn. Chỉ Admin đổi được tác giả.</p>
                  </Field>
                )}
                {!form.authorUser && form.legacyAuthor.trim() && (
                  /* Bài chưa migrate: cho thấy dữ liệu cũ thay vì để người biên tập tưởng mất. */
                  <p className="-mt-2 text-xs text-ink-muted">Tác giả cũ (văn bản): <strong>{form.legacyAuthor}</strong></p>
                )}
                <Field label="Tags"><TagInput value={form.tags} onChange={(tags) => patch({ tags })} placeholder="Enter để thêm" /></Field>
              </div>
            </Card>
            <Card title="Ảnh đại diện"><ImageField value={form.featuredImage} onChange={(m) => patch({ featuredImage: m })} /></Card>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <Card>
            <SeoPanel
              kind="post"
              value={form.seo}
              onChange={(seo) => patch({ seo })}
              pageTitle={form.title}
              slug={`blog/${form.slug}`}
              html={form.content}
              errors={errors}
              settings={site}
              docId={id}
              extra={{
                attachedImages: form.featuredImage ? [{ alt: form.featuredImage.alt }] : [],
                hasExcerpt: !!form.excerpt.trim(),
                hasAuthor: !!form.authorUser || !!form.legacyAuthor.trim(),
                hasPublishedAt: !!form.publishedAt,
                tagCount: form.tags.length,
                faqCount: form.faq.filter((f) => f.question.trim() && f.answer.trim()).length,
              }}
            />
          </Card>
          <Card title="Câu hỏi thường gặp (FAQ trên Google)">
            <FaqEditor items={form.faq} onChange={(faq) => patch({ faq })} errors={errors} />
          </Card>
        </div>
      )}
      <ConfirmDialog open={del} onCancel={() => setDel(false)} onConfirm={remove} loading={busy} title="Xoá bài viết" message={<>Xoá <strong>{form.title}</strong>? Không khôi phục được.</>} />
      <ConfirmDialog open={confirmHide} onCancel={() => setConfirmHide(false)} confirmLabel="Gỡ khỏi web" loading={busy}
        onConfirm={() => { setConfirmHide(false); save({ ...form, published: false }) }}
        title="Gỡ bài khỏi web?" message={<>Khách sẽ không đọc được <strong>{form.title}</strong> nữa; link cũ trả 404.</>} />
      <ConfirmDialog open={confirmSlug !== null} onCancel={() => setConfirmSlug(null)} confirmLabel="Vẫn đổi slug" loading={busy}
        onConfirm={() => { const fn = confirmSlug; setConfirmSlug(null); fn?.() }}
        title="Đổi slug của bài đã đăng?" message={<>Link cũ <code>/blog/{initial?.slug}</code> sẽ được <strong>tự động chuyển 301</strong> sang địa chỉ mới. Không ai gặp 404, nhưng Google cần thời gian chuyển thứ hạng sang URL mới.</>} />
    </>
  )
}
